import axios from "axios";

export default ({ uploadRoute }) => ({
    files: null,
    uploadRoute,
    modelId: null,
    modelType: null,
    attachments: [],
    isDragging: false,
    needReloadTable: false,

    async load(payload) {
        this.modelId = payload.modelId;
        this.modelType = payload.modelType;
        this.files = null;

        try {
            const response = await axios.get(`${this.uploadRoute}/list`, {
                params: {
                    modelType: this.modelType,
                    modelId: this.modelId,
                },
            });

            this.files = response.data?.files ?? [];
        } catch (error) {
            $wireui.notify({
                title: "Error",
                description: "It was not possible to load the attachments",
                icon: "error",
            });

            this.files = [];
        }

        this.show = true;
    },

    get filesAndAttachmentsIsEmpty() {
        return this.files?.length < 1 && this.attachments.length < 1;
    },

    handleDragOver() {
        this.isDragging = true;
    },

    handleDragLeave() {
        this.isDragging = false;
    },

    handleDrop(event) {
        this.isDragging = false;
        event.preventDefault();
        event.stopPropagation();

        const { files } = event.dataTransfer;

        if (files.length > 0) {
            this.attachFiles(files);
        }
    },

    openFileInput() {
        this.$refs.fileInput.click();
    },

    async handleInputChange(event) {
        const { files } = event.target;

        if (files.length > 0) {
            this.attachFiles(files);
        }

        this.$refs.fileInput.value = "";
    },

    handleFileNames(file) {
        const existingNames = new Set([
            ...this.files.map((existingFile) => existingFile.file_name),
        ]);

        const maxLength = 255;
        const { name } = file;

        const lastDotIndex = name.lastIndexOf(".");
        const hasExtension = lastDotIndex !== -1;

        const extension = hasExtension ? name.slice(lastDotIndex) : "";
        let baseName = hasExtension ? name.slice(0, lastDotIndex) : name;

        const maxBaseLength = maxLength - extension.length;
        if (baseName.length > maxBaseLength) {
            baseName = baseName.slice(0, maxBaseLength);
        }

        let finalName = `${baseName}${extension}`;
        let counter = 0;

        const isDuplicate = (name) => {
            return (
                existingNames.has(name) ||
                this.files.some((f) => f.file_name === name)
            );
        };

        while (isDuplicate(finalName)) {
            counter += 1;
            const newBaseName = `${baseName}(${counter})`;

            const trimmedBase = newBaseName.slice(0, maxBaseLength);
            finalName = `${trimmedBase}${extension}`;
        }

        existingNames.add(finalName);

        return new File([file], finalName, {
            type: file.type,
            lastModified: file.lastModified,
        });
    },

    async startUpload() {
        const index = this.attachments.findIndex(
            (attachment) => attachment.finished === false,
        );

        const attachment = this.attachments[index];

        attachment.uploading = true;

        const formData = new FormData();
        formData.append("file", attachment.file);
        formData.append("modelType", this.modelType);
        formData.append("modelId", this.modelId);

        axios
            .post(this.uploadRoute, formData, {
                onUploadProgress(event) {
                    attachment.progress = Math.round(
                        (event.loaded * 100) / event.total,
                    );
                },
                signal: attachment.abort.signal,
            })
            .then(({ data: response }) => {
                this.attachments = this.attachments.filter(
                    ({ id }) => id !== attachment.id,
                );

                this.files.push({
                    ...response.uploaded_file,
                    downloading: false,
                    deleting: false,
                });
            })
            .catch((error) => {
                this.attachments = this.attachments.filter(
                    ({ id }) => id !== attachment.id,
                );

                if (axios.isCancel(error)) {
                    return;
                }

                const message =
                    error.response?.status !== 422
                        ? `It was not possible to upload the attachment <strong>${attachment.file.file_name}</strong>`
                        : error.response?.data.errors.file[0];

                $wireui.notify({
                    title: "Error",
                    description: message,
                    icon: "error",
                });
            })
            .finally(() => {
                attachment.uploading = false;
                attachment.finished = true;

                const nextAttachmentIndex = this.attachments.findIndex(
                    (attachment) => attachment.finished === false,
                );

                if (nextAttachmentIndex !== -1) {
                    this.startUpload();
                }

                this.needReloadTable = true;
            });
    },

    async attachFiles(files) {
        if (files.length > 10) {
            $wireui.notify({
                title: "Error",
                description: "You can only attach up to 10 files at a time.",
                icon: "error",
            });

            return;
        }

        const attachments = [...files].map((file) => {
            const object = {
                id: Math.random().toString(36).substring(2, 9),
                uploading: false,
                finished: false,
                progress: 0,
                abort: new AbortController(),
            };

            object.file = this.handleFileNames(file);

            return object;
        });

        let startUpload = false;

        if (this.attachments.length < 1) {
            startUpload = true;
        }

        attachments.forEach((attachment) => {
            this.attachments.push(attachment);
        });

        if (startUpload) {
            await this.startUpload();
        }
    },

    cancelUpload(id) {
        const attachment = this.attachments.find(
            (attachment) => attachment.id === id,
        );

        if (attachment.uploading) {
            attachment.abort.abort();

            $wireui.notify({
                title: "Error",
                description: `The upload of the attachment <strong>${attachment.file.name}</strong> was canceled`,
                icon: "error",
            });
        }

        this.attachments = this.attachments.filter(
            (attachment) => attachment.id !== id,
        );
    },

    async download(file) {
        if (file.downloading) return;

        file.downloading = true;

        try {
            const req = new XMLHttpRequest();
            req.open("GET", file.url, true);
            req.responseType = "blob";

            req.onload = () => {
                if (req.status === 200) {
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(req.response);
                    link.download = file.file_name;
                    link.click();

                    $wireui.notify({
                        title: "File downloaded",
                        description: `The file was downloaded successfully.`,
                        icon: "success",
                    });
                } else if (req.status === 404) {
                    $wireui.notify({
                        title: "File not available",
                        description: `The file <strong>${file.file_name}</strong> is not available. Please try again later.`,
                        icon: "error",
                    });
                } else {
                    $wireui.notify({
                        title: "Error downloading the file",
                        description: `Could not download <strong>${file.file_name}</strong>.`,
                        icon: "error",
                    });
                }
            };

            req.onerror = () => {
                $wireui.notify({
                    title: "Error downloading the file",
                    description: `Could not download <strong>${file.file_name}</strong>.`,
                    icon: "error",
                });
            };

            req.send();
        } catch (error) {
            console.error("Unexpected error downloading file:", error);

            $wireui.notify({
                title: "Unexpected Error",
                description: `An unexpected error occurred while downloading <strong>${file.file_name}</strong>.`,
                icon: "error",
            });
        } finally {
            setTimeout(() => {
                file.downloading = false;
            }, 1000);
        }
    },

    async deleteFile(file) {
        if (file.deleting) return;

        file.deleting = true;

        const params = {
            tenantId: this.tenantId,
            modelType: this.modelType,
            modelId: this.modelId,
        };

        const response = await axios
            .delete(`${this.uploadRoute}/delete/${file.id}`, { data: params })
            .catch(() => {
                file.deleting = false;
            });

        if (response.status !== 204) {
            $wireui.notify({
                title: "Error",
                description: `It was not possible to delete the attachment <strong>${file.file_name}</strong>`,
                icon: "error",
            });

            file.deleting = false;

            return;
        }

        this.files = this.files.filter(({ id }) => id !== file.id);

        $wireui.notify({
            title: "Attachment deleted",
            description: `The attachment <strong>${file.file_name}</strong> was deleted successfully`,
            icon: "success",
        });

        this.needReloadTable = this.filesAndAttachmentsIsEmpty;

        setTimeout(() => {
            file.deleting = false;
        }, 1000);
    },

    filesizeForHumans(filesize) {
        const mbSize = 1024 * 1024;

        if (filesize < mbSize) {
            return (filesize / 1024).toFixed(2) + "kb";
        }

        return (filesize / mbSize).toFixed(2) + "mb";
    },

    handleClose() {
        this.files = [];
        this.attachments = [];
        this.isDragging = false;
        this.modelId = null;
        this.modelType = null;

        if (this.needReloadTable) {
            this.needReloadTable = false;
            this.$dispatch("refresh-table:orders");
        }
    },
});
